import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * Lands when the user clicks the email link (?token=...).
 *   1. Validates the token on mount via GET /auth/reset-password/validate
 *      so we can show a friendly "Link expired" state before the user
 *      types a new password.
 *   2. On submit, POSTs to /auth/reset-password; redirects to /login on success.
 */
export default function ResetPassword() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const token = sp.get("token") || "";
  const [state, setState] = useState({ phase: "checking", emailHint: null, error: null });
  const [branding, setBranding] = useState(null); // {tenant_name, brand_color, logo_base64, ...}
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) {
      setState({ phase: "invalid", error: "This reset link is missing a token." });
      return;
    }
    (async () => {
      try {
        const r = await api.get("/auth/reset-password/validate", { params: { token } });
        setState({ phase: "ok", emailHint: r.data.email_hint, error: null });
        setBranding(r.data);
        // Apply firm palette CSS vars so hovers + accents match the firm.
        const root = document.documentElement;
        if (r.data?.row_hover) root.style.setProperty("--ll-row-hover", r.data.row_hover);
        if (r.data?.accent) {
          root.style.setProperty("--ll-accent-soft", r.data.accent);
          root.style.setProperty("--ll-mint", r.data.accent);
        }
      } catch (err) {
        setState({ phase: "invalid", error: err?.response?.data?.detail || "This reset link is invalid or has expired." });
      }
    })();
    return () => {
      // Reset vars so public pages don't inherit this firm's palette.
      const root = document.documentElement;
      root.style.removeProperty("--ll-row-hover");
      root.style.removeProperty("--ll-accent-soft");
      root.style.removeProperty("--ll-mint");
    };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (password !== confirm) { toast.error("Passwords don't match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      toast.success("Password updated — sign in with your new password");
      nav("/login");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" style={{ background: "var(--ll-bg)" }}>
      <div
        className="hidden lg:flex flex-col justify-between p-12 border-r relative ll-grain"
        style={{
          borderColor: "var(--ll-border)",
          background: branding?.brand_color || "#0b5d9e",
          color: "white",
        }}
        data-testid="reset-hero"
      >
        <div className="flex items-center gap-2" data-testid="reset-logo">
          {branding?.logo_base64 ? (
            <img src={branding.logo_base64} alt={branding.tenant_name || "logo"} className="h-9 w-auto" style={{ maxWidth: 160 }} />
          ) : (
            <>
              <div className="w-7 h-7 grid place-items-center bg-white text-[var(--ll-ink)]"><span className="font-display font-bold text-sm">F</span></div>
              <div className="font-display font-bold">Financials Ready</div>
            </>
          )}
        </div>
        <div>
          {branding?.logo_base64 && (
            <img
              src={branding.logo_base64}
              alt={branding.tenant_name || "logo"}
              className="mb-6 h-16 w-auto"
              style={{ maxWidth: 260 }}
              data-testid="reset-hero-logo"
            />
          )}
          <div className="text-xs uppercase tracking-[0.25em] text-white/50 mb-3">Password reset</div>
          <div className="font-display font-bold text-4xl xl:text-5xl leading-tight" data-testid="reset-hero-title">
            {branding?.tenant_name ? <>Fresh start at <span className="italic" style={{ color: "var(--ll-ochre)" }}>{branding.tenant_name}</span>.</> : "Fresh start."}
          </div>
          <p className="mt-4 text-base/relaxed text-white max-w-md">Pick a new password. We&apos;ll sign all your old sessions out so no one else is still logged in.</p>
        </div>
        <div className="text-xs uppercase tracking-[0.25em] text-white/50">Made for finance</div>
      </div>

      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm" data-testid="reset-password-card">
          <div className="text-xs uppercase tracking-[0.2em] text-[var(--ll-ink-muted)] mb-3">Set new password</div>
          {state.phase === "checking" && (
            <div className="text-sm text-[var(--ll-ink-soft)] flex items-center gap-2" data-testid="reset-checking">
              <Loader2 size={16} className="animate-spin" /> Checking link…
            </div>
          )}
          {state.phase === "invalid" && (
            <div data-testid="reset-invalid">
              <div className="font-display font-bold text-3xl mb-2 flex items-center gap-2">
                <AlertTriangle size={24} className="text-[var(--ll-error)]" /> Link expired
              </div>
              <p className="text-sm text-[var(--ll-ink-soft)] mb-6">
                {state.error} For your security, reset links expire after 30 minutes and can only be used once.
              </p>
              <Link to="/forgot-password" data-testid="reset-request-new" className="inline-flex items-center gap-2 bg-[var(--ll-ink)] text-white px-5 py-2.5 hover:bg-[#0b5d9e]">
                Request a new link <ArrowRight size={14} />
              </Link>
            </div>
          )}
          {state.phase === "ok" && (
            <>
              <div className="font-display font-bold text-3xl mb-2 flex items-center gap-2">
                <CheckCircle2 size={22} className="text-[var(--ll-success)]" /> You&apos;re good to go
              </div>
              {state.emailHint && (
                <p className="text-sm text-[var(--ll-ink-soft)] mb-5">
                  Resetting the password for <b className="font-mono-fin">{state.emailHint}</b>.
                </p>
              )}
              <form onSubmit={submit}>
                <label className="block text-xs uppercase tracking-widest text-[var(--ll-ink-soft)] mb-1">New password</label>
                <input
                  type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                  minLength={8} autoFocus
                  data-testid="reset-new-password"
                  className="w-full px-3 py-2.5 border bg-white mb-3"
                  style={{ borderColor: "var(--ll-border)" }}
                  placeholder="At least 8 characters"
                />
                <label className="block text-xs uppercase tracking-widest text-[var(--ll-ink-soft)] mb-1">Confirm password</label>
                <input
                  type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required
                  minLength={8}
                  data-testid="reset-confirm-password"
                  className="w-full px-3 py-2.5 border bg-white mb-5"
                  style={{ borderColor: "var(--ll-border)" }}
                  placeholder="Retype your new password"
                />
                <button
                  type="submit" disabled={busy || !password || !confirm}
                  data-testid="reset-submit"
                  className="w-full bg-[var(--ll-ink)] text-white py-3 hover:bg-[#0b5d9e] transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {busy ? "Saving…" : <>Update password <ArrowRight size={15} /></>}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
